import { observer } from "mobx-react-lite";
import { useMemo, useRef } from "react";

import { NativeSelect } from "../base/native-select";
import { useFormFieldId } from "../form/form-field-id-context";
import {
  EnumValues,
  FieldProps,
  useField,
  useModelBinding,
  valuesToMap,
} from "./utils";
import { ValidationTooltip } from "./validation-tooltip";

interface Props<M extends object, V extends string | number> extends FieldProps<
  M,
  V
> {
  values: EnumValues<V>;
  listTitle?: string;
  required?: boolean;
  disabled?: boolean;
  /** For selects with no visible label, which would otherwise be unnamed. */
  ariaLabel?: string;
}

export const Select = observer(function Select<
  M extends object,
  V extends string | number,
>({
  values,
  listTitle,
  required,
  disabled,
  ariaLabel,
  ...restProps
}: Props<M, V>) {
  const formFieldId = useFormFieldId();
  const elementRef = useRef<HTMLSelectElement>(null);
  const { isInvalid, tooltip } = useField(elementRef);

  const { value, onChange } = useModelBinding(restProps);

  const valuesMap = useMemo(() => valuesToMap(values), [values]);
  const isNumber = useMemo(
    () => typeof valuesMap.keys().next().value === "number",
    [valuesMap],
  );

  // Check if current value is empty or doesn't match any option
  const hasEmptyValue =
    value === undefined || value === null || value === ("" as any);
  const valueMatchesOption = !hasEmptyValue && valuesMap.has(value);
  const showPlaceholder = !valueMatchesOption;

  return (
    <ValidationTooltip model={tooltip}>
      <NativeSelect
        id={formFieldId}
        value={value ?? ""}
        onChange={(event) => {
          const raw = event.target.value;
          onChange((raw === "" ? null : isNumber ? parseInt(raw) : raw) as V);
        }}
        required={required}
        ref={elementRef}
        aria-invalid={isInvalid}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        {(showPlaceholder || !required) && (
          <option value="" disabled={required}>
            {listTitle ?? "—"}
          </option>
        )}

        {[...valuesMap.entries()].map(([val, displayValue]) => (
          <option key={val} value={val}>
            {displayValue}
          </option>
        ))}
      </NativeSelect>
    </ValidationTooltip>
  );
});
